<template>
  <div class="ship-layer-container">
    <div ref="shipLayerContainer" class="ship-g6-layer"></div>
  </div>
</template>

<script>
import G6 from '@antv/g6'
import L from 'leaflet'

export default {
  name: 'ShipG6Layer',
  props: {
    ships: {
      type: Array,
      required: true
    },
    map: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      graph: null,
      nodes: [],
      layerInitialized: false,
      initRetryCount: 0,
      _lastUpdateTime: null,
      _throttleTimer: null, // 用于节流
      _currentZooming: false // 标记当前是否在缩放过程中
    }
  },
  watch: {
    ships: {
      handler() {
        this.updateShips();
      },
      deep: true
    },
    map: {
      handler(newMap) {
        if (newMap && !this.layerInitialized) {
          // 延迟一下初始化，确保DOM完全渲染
          setTimeout(() => {
            this.initG6Layer();
          }, 200);
        }
      },
      immediate: true
    }
  },
  mounted() {
    console.log('ShipG6Layer mounted');
    // 使用nextTick确保DOM已经渲染
    this.$nextTick(() => {
      console.log('ShipG6Layer nextTick');
      if (this.map) {
        // 延迟初始化，确保DOM渲染完成
        setTimeout(() => {
          this.initG6Layer();
        }, 200);
      }
    });
  },
  beforeDestroy() {
    this.removeEventListeners();
    if (this.graph) {
      this.graph.destroy();
    }
    // 清除可能存在的节流定时器
    if (this._throttleTimer) {
      clearTimeout(this._throttleTimer);
    }
  },
  methods: {
    initG6Layer() {
      if (!this.map || this.layerInitialized) return;
      
      console.log('初始化G6图层', ++this.initRetryCount);
      
      try {
        // 确保容器元素存在
        if (!this.$refs.shipLayerContainer) {
          console.error('G6容器元素引用不存在', this.initRetryCount);
          // 延迟重试
          if (this.initRetryCount < 5) {
            setTimeout(() => {
              this.initG6Layer();
            }, 300);
          }
          return;
        }
        
        // 获取地图尺寸
        const size = this.map.getSize();
        console.log('地图尺寸:', size);
        
        // 确保CSS正确设置点击捕获
        const container = this.$refs.shipLayerContainer;
        if (container) {
          // 设置容器样式
          container.style.position = 'absolute';
          container.style.top = '0';
          container.style.left = '0';
          container.style.width = '100%';
          container.style.height = '100%';
          container.style.zIndex = '399'; // 确保在地图上方，但在控件下方
          
          // 重要：确保容器不拦截事件
          container.style.pointerEvents = 'none';
        }
        
        // 注册自定义船舶图形，增加点击区域
        G6.registerNode('ship-node', {
          draw(cfg, group) {
            const size = cfg.size || 14; // 增大尺寸提高可点击性
            
            // 添加一个透明的圆形作为点击区域
            const hitArea = group.addShape('circle', {
              attrs: {
                x: 0,
                y: 0,
                r: size * 1.5, // 比实际图标大一些
                fill: 'transparent',
                cursor: 'pointer',
              },
              name: 'ship-hit-area',
              draggable: false,
            });
            
            // 创建船舶三角形
            const shape = group.addShape('path', {
              attrs: {
                path: `M0,${-size} L${size * 0.6},${size * 0.8} L0,${size * 0.3} L${-size * 0.6},${size * 0.8} Z`,
                fill: '#3a5eea',
                stroke: '#1c2f7b',
                lineWidth: 1.5,
                cursor: 'pointer',
              },
              name: 'ship-path',
              draggable: false,
            });
            
            return shape; // 返回主形状
          },
          // 设置状态样式
          setState(name, value, item) {
            const group = item.getContainer();
            const path = group.find(e => e.get('name') === 'ship-path');
            
            if (name === 'hover') {
              if (value) {
                path.attr({
                  fill: '#4979ff',
                  stroke: '#2e4eb8',
                  lineWidth: 2,
                  shadowColor: '#1c2f7b',
                  shadowBlur: 5,
                });
              } else {
                path.attr({
                  fill: '#3a5eea',
                  stroke: '#1c2f7b',
                  lineWidth: 1.5,
                  shadowBlur: 0,
                });
              }
            }
            
            if (name === 'selected') {
              if (value) {
                path.attr({
                  fill: '#ff7043',
                  stroke: '#d84315',
                  lineWidth: 2,
                });
              } else {
                path.attr({
                  fill: '#3a5eea',
                  stroke: '#1c2f7b',
                  lineWidth: 1.5,
                });
              }
            }
          }
        });
        
        // 创建G6图实例
        this.graph = new G6.Graph({
          container: this.$refs.shipLayerContainer,
          width: size.x,
          height: size.y,
          animate: false, // 关闭动画提高性能
          modes: {
            default: [], // 禁用默认的drag-canvas模式，以免与地图操作冲突
          },
          defaultNode: {
            type: 'ship-node',
            size: 14,
          },
        });
        
        // 绑定船舶点击事件
        this.graph.on('node:click', (evt) => {
          // 记录点击位置以便调试
          console.log('船舶点击坐标:', evt.x, evt.y);
          
          // 阻止事件传播
          evt.originalEvent.stopPropagation();
          evt.preventDefault();
          
          // 获取节点数据并发送点击事件
          const model = evt.item.getModel();
          if (model && model.shipData) {
            console.log('点击船舶:', model.shipData.userid);
            this.$emit('ship-click', model.shipData);
            
            // 设置选中状态
            this.graph.findAllByState('node', 'selected').forEach(node => {
              this.graph.setItemState(node, 'selected', false);
            });
            this.graph.setItemState(evt.item, 'selected', true);
          }
        });
        
        // 鼠标hover效果
        this.graph.on('node:mouseenter', (evt) => {
          this.graph.setItemState(evt.item, 'hover', true);
        });
        
        this.graph.on('node:mouseleave', (evt) => {
          this.graph.setItemState(evt.item, 'hover', false);
        });
        
        // 绑定地图事件
        this.map.on('move', this.onMapMove);
        this.map.on('zoom', this.onMapZoom);
        this.map.on('moveend', this.onMapMove);
        this.map.on('zoomend', this.onMapZoomEnd);
        this.map.on('resize', this.onMapResize);
        
        // 绑定缩放动画事件，实现缩放过程中的实时更新
        this.map.on('zoomanim', this.onMapZoomAnim);
        
        this.layerInitialized = true;
        console.log('G6图层初始化完成');
        
        // 首次更新船舶
        this.updateShips();
      } catch (err) {
        console.error('G6图层初始化错误:', err);
      }
    },
    
    // 节流函数，限制更新频率
    throttledUpdateShips() {
      if (this._throttleTimer) return;
      
      this._throttleTimer = setTimeout(() => {
        this.updateShips();
        this._throttleTimer = null;
      }, 16); // 约60fps的更新频率
    },
    
    // 缩放动画中的处理
    onMapZoomAnim(e) {
      this._currentZooming = true;
      
      try {
        // 获取当前缩放级别和目标缩放级别
        const currentZoom = this.map.getZoom();
        const scale = this.map.getZoomScale(e.zoom, currentZoom);
        
        // 获取缩放中心点
        const offset = this.map._latLngToNewLayerPoint(
          this.map.getCenter(),
          e.zoom,
          e.center
        );
        
        // 应用CSS变换实现平滑过渡
        const container = this.$refs.shipLayerContainer;
        if (container) {
          container.style.transformOrigin = '0 0';
          container.style.transform = `translate(${offset.x}px, ${offset.y}px) scale(${scale})`;
          container.style.transition = 'transform 0.25s cubic-bezier(0,0,0.25,1)';
        }
      } catch (err) {
        console.error('缩放动画处理错误:', err);
      }
      
      // 在缩放过程中使用节流更新
      this.throttledUpdateShips();
    },
    
    updateShips() {
      if (!this.map || !this.graph || !this.ships) return;
      
      try {
        // 如果正在缩放中，重置变换
        if (this._currentZooming) {
          const container = this.$refs.shipLayerContainer;
          if (container) {
            container.style.transform = '';
            container.style.transition = '';
          }
          this._currentZooming = false;
        }
        
        // 转换船舶数据
        const shipNodes = this.ships
          .filter(ship => ship && ship.pos)
          .map(ship => {
            try {
              const latlng = L.latLng(ship.pos.latitude, ship.pos.longitude);
              const point = this.map.latLngToContainerPoint(latlng);
              return {
                id: `ship-${ship.userid}`,
                x: point.x,
                y: point.y,
                shipData: ship,
                cog: ship.cog !== undefined ? ship.cog / 10 : 0,
                sog: ship.sog !== undefined ? ship.sog / 10 : 0,
                mmsi: ship.userid,
                label: ship.userid, // 添加标签用于调试
                style: {
                  cursor: 'pointer' // 确保鼠标指针样式正确
                }
              };
            } catch (e) {
              console.error('船舶数据转换错误:', e, ship);
              return null;
            }
          })
          .filter(Boolean); // 过滤掉转换失败的船舶
        
        // 清空现有数据并添加新数据
        const data = {
          nodes: shipNodes
        };
        
        this.graph.changeData(data);
        
        // 更新旋转角度
        shipNodes.forEach(node => {
          const nodeItem = this.graph.findById(node.id);
          if (nodeItem) {
            const group = nodeItem.getContainer();
            const path = group.find(element => element.get('name') === 'ship-path');
            if (path) {
              path.rotate(node.cog * Math.PI / 180);
            }
          }
        });
        
        // 刷新图形
        this.graph.refresh();
      } catch (err) {
        console.error('更新船舶图层错误:', err);
      }
    },
    
    // 地图事件处理
    onMapMove() {
      this.updateShips();
    },
    
    onMapZoom() {
      // 标记缩放开始
      this._currentZooming = true;
      this.throttledUpdateShips();
    },
    
    onMapZoomEnd() {
      // 缩放结束，重置状态并更新一次
      this._currentZooming = false;
      const container = this.$refs.shipLayerContainer;
      if (container) {
        container.style.transform = '';
        container.style.transition = '';
      }
      this.updateShips();
    },
    
    onMapResize() {
      if (this.graph && this.map) {
        const size = this.map.getSize();
        this.graph.changeSize(size.x, size.y);
        this.updateShips();
      }
    },
    
    removeEventListeners() {
      if (this.map) {
        this.map.off('move', this.onMapMove);
        this.map.off('zoom', this.onMapZoom);
        this.map.off('zoomanim', this.onMapZoomAnim);
        this.map.off('moveend', this.onMapMove);
        this.map.off('zoomend', this.onMapZoomEnd);
        this.map.off('resize', this.onMapResize);
      }
    }
  }
};
</script>

<style scoped>
.ship-layer-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 399;  /* 确保在地图瓦片之上，但在控件之下 */
  pointer-events: none;  /* 允许鼠标事件穿透到地图 */
}

.ship-g6-layer {
  width: 100%;
  height: 100%;
  pointer-events: none; /* 允许事件穿透 */
}
</style>

<style>
/* 全局样式：确保G6的节点能接收鼠标事件 */
.g6-node {
  pointer-events: auto !important;
  cursor: pointer !important;
}

/* 确保G6的边缘和背景不接收鼠标事件 */
.g6-edge, .g6-background {
  pointer-events: none !important;
}
</style> 